import * as React from 'react';

import Entity from '../entity';

import { CalloutCarousel } from '.';

function isLeftColumnSplit(
  value: React.ReactNode | [React.ReactNode, React.ReactNode]
): value is [React.ReactNode, React.ReactNode] {
  return Array.isArray(value) && value.length === 2;
}

interface ICalloutPaneBodyProps {
  /** Filters passed to CalloutCarousel (e.g. merchantId, financialAccountId) */
  carouselFilters?: {
    merchantId?: string;
    financialAccountId?: string;
  };
  /**
   * Left column. Single node: no split; below xl: callouts, right, left. [first, second] tuple:
   * split; below xl: callouts, first, right, second.
   */
  leftColumn: React.ReactNode | [React.ReactNode, React.ReactNode];
  /** Right column. With split, inserted between the two segments below xl. */
  rightColumn?: React.ReactNode;
  /** Optional className passed to Entity.Body */
  className?: string;
}

/**
 * Reusable layout wrapper that positions the CalloutCarousel based on callout count:
 * - 0-1 callouts: carousel in left column, right column aligns to top
 * - 2+ callouts: carousel spans full width above both columns
 */
const CalloutPaneBody = ({ carouselFilters, leftColumn, rightColumn, className }: ICalloutPaneBodyProps) => {
  const [calloutCount, setCalloutCount] = React.useState(0);

  const carousel = <CalloutCarousel filters={carouselFilters} onCalloutCountChange={setCalloutCount} />;

  const singleColumnOrderOverride = isLeftColumnSplit(leftColumn)
    ? rightColumn != null
      ? [carousel, leftColumn[0], rightColumn, leftColumn[1]]
      : [carousel, leftColumn[0], leftColumn[1]]
    : rightColumn != null
      ? [carousel, rightColumn, leftColumn]
      : undefined;

  // 2+ callouts: carousel as full-width primaryElement above columns
  if (calloutCount >= 2) {
    return (
      <Entity.Body
        primaryElement={carousel}
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        singleColumnOrderOverride={singleColumnOrderOverride}
        className={className}
      />
    );
  }

  // 0-1 callouts: carousel in left column, right column aligns to top
  return (
    <Entity.Body
      leftColumn={
        <>
          {carousel}
          {leftColumn}
        </>
      }
      rightColumn={rightColumn}
      singleColumnOrderOverride={singleColumnOrderOverride}
      className={className}
    />
  );
};

export default CalloutPaneBody;
